import { useState, useEffect, useCallback } from 'react';

interface BannerData {
  active: boolean;
  benefitsMe?: boolean;
  reason?: string;
  discountType?: 'percentage' | 'flat';
  endsAt?: string;
  ordersUsed?: number;
  ordersCap?: number | null;
}

export default function PromoBanner() {
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [countdown, setCountdown] = useState('');

  const fetchBanner = useCallback(() => {
    const token = localStorage.getItem('dealer_auth_token') || localStorage.getItem('auth_token');
    if (!token) return;
    fetch('/api/promotions/banner', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data) { setBanner(null); return; }
        // Check dismiss state — re-show if new promo (different endsAt)
        const dismissKey = sessionStorage.getItem('promo_banner_dismissed_ends');
        if (dismissKey && dismissKey === data.endsAt) {
          setDismissed(true);
        } else {
          setDismissed(false);
        }
        setBanner(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchBanner();
    const interval = setInterval(fetchBanner, 300000); // 5 min
    return () => clearInterval(interval);
  }, [fetchBanner]);

  // Countdown timer
  useEffect(() => {
    if (!banner?.endsAt) return;
    const update = () => {
      const diff = new Date(banner.endsAt!).getTime() - Date.now();
      if (diff <= 0) { setCountdown(''); fetchBanner(); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (d > 0) setCountdown(`${d}d ${h}h ${m}m ${s}s`);
      else if (h > 0) setCountdown(`${h}h ${m}m ${s}s`);
      else setCountdown(`${m}m ${s}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [banner?.endsAt, fetchBanner]);

  const handleDismiss = () => {
    setDismissed(true);
    if (banner?.endsAt) {
      sessionStorage.setItem('promo_banner_dismissed_ends', banner.endsAt);
    }
  };

  // Cap reached — subdued note
  if (banner?.active && !banner.benefitsMe && banner.reason === 'cap_reached') {
    return (
      <div className="bg-gray-100 text-gray-500 text-center py-2 text-sm">
        You've reached the promotional order limit for this event
      </div>
    );
  }

  // Only show when benefits dealer
  if (!banner?.active || !banner.benefitsMe || dismissed) return null;

  const hasOrderCap = banner.ordersCap != null && banner.ordersCap > 0;

  const message = banner.discountType === 'flat'
    ? 'Limited-Time Offer — save on every screen unit'
    : 'Special Promotion — your pricing reflects current promotional rates';

  const capMessage = hasOrderCap
    ? ` · ${banner.ordersUsed || 0} of ${banner.ordersCap} promo orders used`
    : '';

  return (
    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium">
            {message}{capMessage} · Ends in {countdown}
          </p>
          {hasOrderCap && (
            <div className="mt-1.5 w-48 bg-white/30 rounded-full h-2">
              <div
                className="bg-white rounded-full h-2 transition-all"
                style={{ width: `${Math.min(100, ((banner.ordersUsed || 0) / (banner.ordersCap || 1)) * 100)}%` }}
              />
            </div>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className="ml-4 text-white/70 hover:text-white text-lg leading-none"
          title="Dismiss"
        >
          &times;
        </button>
      </div>
    </div>
  );
}
